import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Pagination,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  deserify,
  getLocalStorage,
  IStyles,
  pxToRem,
  pxTovW,
  SearchBar,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { QuestionPaperType } from '@protos/content_management/content.db_pb';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { useDispatch } from 'react-redux';
import { setQuestionPaperData } from '../reducer/homeDashboard.slice';
import { QuestionPaperTable } from './components/QuestionPaperTable';
import {
  getBoardArrayQP,
  getGradeArrayQP,
  getSubjectArrayQP,
  getYearsArrayQP,
  IQuestionPaperFilter,
  createBoardListFromResponse,
  createClassListFromResponse,
  createSubjectListFromResponse,
  createYearListFromResponse,
} from '../home.utils';
import { FiltersContainerQP } from './components/FiltersContainerQP';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(240) },
    paddingY: { xs: pxToRem(15), md: pxTovW(20) },
    display: 'flex',
    flexDirection: 'column',
    height: { xs: '91vh', md: 'unset' },
    boxSizing: 'border-box',
    justifyContent: 'flex-start',
  },

  tabBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(65) },
    marginTop: { xs: pxToRem(20), md: pxTovW(35) },
    marginBottom: { xs: pxToRem(20), md: pxTovW(30) },
  },
  selectedTab: {
    backgroundColor: 'primary.main',
    paddingX: { xs: pxToRem(7), md: pxTovW(14) },
    paddingY: { xs: pxToRem(10), md: pxTovW(20) },
    '&:hover': {
      backgroundColor: '#006BB5',
    },
  },
  notSelectedTab: {
    paddingX: { xs: pxToRem(7), md: pxTovW(14) },
    paddingY: { xs: pxToRem(10), md: pxTovW(20) },
    '&:hover': {
      backgroundColor: '#E6F3FB',
    },
  },

  allInputBox: {
    display: { xs: 'block', md: 'flex' },
    gap: { xs: pxToRem(15), md: pxTovW(30) },
    alignItems: 'end',
    justifyContent: 'center',
  },
  inputContainer: {
    display: 'flex',
    gap: { xs: pxToRem(15), md: pxTovW(30) },
    alignItems: 'end',
    justifyContent: 'space-between', // optional, helps spacing
    '& > *': { flex: 1 },
    marginTop: { xs: pxToRem(15), md: pxTovW(0) },
  },

  // CheckBoxMenuItem
  checkboxWithItem: {
    borderBottom: '1px solid #96FFC5',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: '#ECFFF5' },
    paddingX: { xs: pxToRem(5), md: pxTovW(10) },
    paddingY: { xs: pxToRem(6), md: pxTovW(12) },
    display: 'flex',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
    alignItems: 'center',
    borderRadius: { xs: pxToRem(3), md: pxTovW(3) },
  },
  checkboxIcon: {
    width: { xs: pxToRem(29), md: pxTovW(20) },
    height: { xs: pxToRem(29), md: pxTovW(20) },
    backgroundColor: 'white',
    border: '1px solid #93CEFC',
    borderRadius: { xs: pxToRem(3), md: pxTovW(3) },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  checkbox: {
    padding: 0,
    boxSizing: 'border-box',
    maxWidth: 'max-content',
  },
};

export interface IGetQuestionPapers {
  currentSearchText?: string;
  currentBoardsList?: IQuestionPaperFilter[];
  currentClassesList?: IQuestionPaperFilter[];
  currentSubjectsList?: IQuestionPaperFilter[];
  currentYearList?: IQuestionPaperFilter[];
}
export const PreviousYearQPList = () => {
  const teacher_id = getLocalStorage('userId');
  const { ContentParserAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { question_paper_data } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );

  const [loadingState, setLoadingState] = useState<
    'loading' | 'success' | 'error'
  >('success');
  const tabNames = [QuestionPaperType.PREVIOUS_YEAR, QuestionPaperType.SAMPLE];
  const [searchText, setSearchText] = useState('');
  const [boardsList, setBoardsList] = useState<IQuestionPaperFilter[]>([]);
  const [classesList, setClassesList] = useState<IQuestionPaperFilter[]>([]);
  const [subjectsList, setSubjectsList] = useState<IQuestionPaperFilter[]>([]);
  const [yearsList, setYearsList] = useState<IQuestionPaperFilter[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasSamplePapers, setHasSamplePapers] = useState(false);

  // ------------------------------------------------------------

  const areThereAnySamplePapers = async () => {
    try {
      const response =
        await ContentParserAPIServiceV1ClientWithStatusCodeHandler.getQuestionPapers(
          {
            questionPaperType: QuestionPaperType.SAMPLE,
            teacherId: BigInt(teacher_id),
          }
        );
      setHasSamplePapers(response.totalQuestionPapers > 0);
    } catch (err) {
      console.log(err);
      setHasSamplePapers(false);
    }
  };

  // ------------------------------------------------------------

  const getQuestionPapers = async (props?: IGetQuestionPapers) => {
    const {
      currentSearchText,
      currentBoardsList,
      currentClassesList,
      currentSubjectsList,
      currentYearList,
    } = props || {};

    const PAGE_LIMIT = 7;
    try {
      setLoadingState('loading');
      const response =
        await ContentParserAPIServiceV1ClientWithStatusCodeHandler.getQuestionPapers(
          {
            questionPaperType: question_paper_data?.selected_tab,
            searchText: currentSearchText ?? searchText,
            boards: getBoardArrayQP(currentBoardsList ?? boardsList),
            grades: getGradeArrayQP(currentClassesList ?? classesList),
            subjects: getSubjectArrayQP(currentSubjectsList ?? subjectsList),
            academicYears: getYearsArrayQP(currentYearList ?? yearsList),
            page: pageNumber,
            limit: PAGE_LIMIT,
            teacherId: BigInt(teacher_id),
          }
        );
      if (response) {
        setLoadingState('success');
        const updatedData = {
          ...question_paper_data,
          total_pages: Math.ceil(response.totalQuestionPapers / PAGE_LIMIT),
        };
        if (
          question_paper_data?.selected_tab === QuestionPaperType.PREVIOUS_YEAR
        ) {
          updatedData.previous_year_list = {
            ...question_paper_data.previous_year_list,
            [pageNumber]: response.questionPapers,
          };
        } else {
          updatedData.sample_paper_list = {
            ...question_paper_data?.sample_paper_list,
            [pageNumber]: response.questionPapers,
          };
        }
        dispatch(setQuestionPaperData(updatedData));

        if (!boardsList?.length && response.boardList?.length) {
          setBoardsList(createBoardListFromResponse(response.boardList));
        }

        if (!classesList?.length && response.gradeList?.length) {
          setClassesList(createClassListFromResponse(response.gradeList));
        }

        if (!subjectsList?.length && response.subjectList?.length) {
          setSubjectsList(createSubjectListFromResponse(response.subjectList));
        }

        if (!yearsList?.length && response.academicYearList?.length) {
          setYearsList(createYearListFromResponse(response.academicYearList));
        }
      } else {
        setLoadingState('error');
      }
    } catch (err) {
      console.log(err);
      setLoadingState('error');
    }
  };

  // ------------------------------------------------------------

  useEffect(() => {
    getQuestionPapers();
  }, [question_paper_data?.selected_tab, pageNumber]);

  // ------------------------------------------------------------

  useEffect(() => {
    areThereAnySamplePapers();
  }, []);

  // ------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Typography variant="h1">Previous Year Question Papers</Typography>

      <Box sx={styles.tabBox}>
        {tabNames.map((tabName, index) =>
          tabName === QuestionPaperType.SAMPLE && !hasSamplePapers ? null : (
            <Button
              key={index}
              disabled={loadingState === 'loading'}
              onClick={() => {
                if (question_paper_data?.selected_tab === tabName) return;
                dispatch(
                  setQuestionPaperData({
                    ...question_paper_data,
                    selected_tab: tabName,
                  })
                );
                setBoardsList([]);
                setClassesList([]);
                setSubjectsList([]);
                setYearsList([]);
                setPageNumber(1);
              }}
              sx={
                question_paper_data?.selected_tab === tabName
                  ? styles.selectedTab
                  : styles.notSelectedTab
              }
            >
              <Typography
                variant="h3"
                fontWeight="regular"
                textAlign="center"
                color={
                  question_paper_data?.selected_tab === tabName
                    ? 'common.white'
                    : 'initial'
                }
              >
                {tabName === QuestionPaperType.PREVIOUS_YEAR
                  ? 'Previous Year Question Papers'
                  : 'Sample Papers'}
              </Typography>
            </Button>
          )
        )}
      </Box>

      <Box sx={styles.allInputBox}>
        {/* <SearchBar
          searchText={searchText}
          handleSearchTextChange={setSearchText}
          handleSearch={() => {
            setPageNumber(1);
            getQuestionPapers();
          }}
          handleClose={() => {
            setSearchText('');
            setPageNumber(1);
            getQuestionPapers({ currentSearchText: '' });
          }}
        /> */}

        {isMobile ? (
          <Accordion
            sx={{
              marginTop: pxToRem(25),
              border: '1px solid #007CDCA6',
              borderRadius: pxToRem(10),
            }}
            square
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{
                '&.MuiAccordionSummary-root': {
                  borderBottom: '1px solid #007CDC42',
                },
              }}
            >
              <Typography variant="h3" fontWeight="bold">
                Filters
              </Typography>
            </AccordionSummary>

            <AccordionDetails
              sx={{ borderRadius: { xs: pxToRem(10), md: pxTovW(0) } }}
            >
              <FiltersContainerQP
                boardsList={boardsList}
                setBoardsList={setBoardsList}
                classesList={classesList}
                setClassesList={setClassesList}
                subjectsList={subjectsList}
                setSubjectsList={setSubjectsList}
                yearsList={yearsList}
                setYearsList={setYearsList}
                setPageNumber={setPageNumber}
                getQuestionPapers={getQuestionPapers}
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          <FiltersContainerQP
            boardsList={boardsList}
            setBoardsList={setBoardsList}
            classesList={classesList}
            setClassesList={setClassesList}
            subjectsList={subjectsList}
            setSubjectsList={setSubjectsList}
            yearsList={yearsList}
            setYearsList={setYearsList}
            setPageNumber={setPageNumber}
            getQuestionPapers={getQuestionPapers}
          />
        )}
      </Box>

      <QuestionPaperTable loadingState={loadingState} pageNumber={pageNumber} />

      {question_paper_data?.total_pages &&
      question_paper_data?.total_pages > 0 ? (
        <Pagination
          size={isMobile ? 'medium' : 'large'}
          page={pageNumber}
          count={question_paper_data?.total_pages}
          color="primary"
          sx={{ width: 'max-content', margin: 'auto' }}
          onChange={(e, val) => {
            setPageNumber(val);
          }}
        />
      ) : null}
    </Box>
  );
};

// ------------------------------------------------------------
